/**
 * BMI Calculator
 *
 * Interactive BMI calculator with unit toggling (metric/imperial),
 * visual gauge, and further consultation lead-capture form.
 */

const BMI_CATEGORIES = [
  {
    category: 'Malnourished',
    range: [0, 16],
    color: '#6366f1',
    content:
      'With this rate you are easily affected by critical health conditions. Contact CPAP to Address BMI for Better Well-being, Reduced Health Risks.',
  },
  {
    category: 'Underweight',
    range: [16, 18.5],
    color: '#3b82f6',
    content:
      'Take Charge of Your Health with CPAP Discount Warehouse. We are Here to Help You Understand How Your BMI Impacts Your Well-being.',
  },
  {
    category: 'Fit',
    range: [18.5, 25],
    color: '#22c55e',
    content:
      'Stay on the Path to Optimal Health with CPAP Discount Warehouse. Whether You Maintain a Healthy BMI or Seek Guidance, We will Clarify How It Relates to Your Well-being.',
  },
  {
    category: 'Overweight',
    range: [25, 30],
    color: '#f59e0b',
    content:
      'Ready to Improve Your Health and Understand the Role of BMI? CPAP Discount Warehouse is Dedicated to Enhancing Your Well-being.',
  },
  {
    category: 'Obesity',
    range: [30, 100],
    color: '#ef4444',
    content:
      'Step into a Healthier Life with Confidence. At CPAP Discount Warehouse, We are Committed to Clarifying How Your BMI Impacts Your Health and Helping You Achieve Vitality.',
  },
];

/** Maximum BMI value shown on the gauge */
const GAUGE_MAX = 40;

class BmiCalculator extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-bmi-form]');
    this.clearBtn = this.querySelector('[data-bmi-clear]');
    this.step1 = this.querySelector('[data-bmi-step="1"]');
    this.step2 = this.querySelector('[data-bmi-step="2"]');
    this.backBtn = this.querySelector('[data-bmi-back]');

    // Unit toggles
    this.heightUnitRadios = this.querySelectorAll('input[name="height_unit"]');
    this.weightUnitRadios = this.querySelectorAll('input[name="weight_unit"]');

    // Input groups
    this.metricHeightGroup = this.querySelector('[data-height-metric]');
    this.imperialHeightGroup = this.querySelector('[data-height-imperial]');

    // Result elements
    this.scoreEl = this.querySelector('[data-bmi-score]');
    this.rangeEl = this.querySelector('[data-bmi-range]');
    this.categoryEl = this.querySelector('[data-bmi-category]');
    this.descriptionEl = this.querySelector('[data-bmi-description]');
    this.needleEl = this.querySelector('[data-bmi-needle]');

    // Consultation form
    this.consultForm = this.querySelector('[data-consult-form]');
    this.consultSuccess = this.querySelector('[data-consult-success]');

    this._bindEvents();
  }

  _bindEvents() {
    // Form submission
    this.form?.addEventListener('submit', (e) => {
      e.preventDefault();
      this._handleCalculate();
    });

    // Clear button
    this.clearBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      this._clearForm();
    });

    // Back button
    this.backBtn?.addEventListener('click', () => {
      this._showStep(1);
    });

    // Height unit toggle
    this.heightUnitRadios.forEach((radio) => {
      radio.addEventListener('change', () => this._toggleHeightUnit());
    });

    // Weight unit toggle
    this.weightUnitRadios.forEach((radio) => {
      radio.addEventListener('change', () => this._toggleWeightUnit());
    });

    // Consultation form
    this.consultForm?.addEventListener('submit', (e) => {
      e.preventDefault();
      this._handleConsultSubmit();
    });
  }

  /**
   * Get the currently selected height unit.
   * @returns {'cm'|'ft'}
   */
  _getHeightUnit() {
    const checked = this.querySelector('input[name="height_unit"]:checked');
    return checked ? checked.value : 'cm';
  }

  /**
   * Get the currently selected weight unit.
   * @returns {'kg'|'lbs'}
   */
  _getWeightUnit() {
    const checked = this.querySelector('input[name="weight_unit"]:checked');
    return checked ? checked.value : 'kg';
  }

  /** Show/hide metric vs imperial height fields */
  _toggleHeightUnit() {
    const unit = this._getHeightUnit();
    if (this.metricHeightGroup && this.imperialHeightGroup) {
      this.metricHeightGroup.style.display = unit === 'cm' ? '' : 'none';
      this.imperialHeightGroup.style.display = unit === 'ft' ? '' : 'none';
    }
    // Update the unit label for weight suffix
    const weightSuffix = this.querySelector('[data-weight-unit-label]');
    if (weightSuffix) {
      weightSuffix.textContent = this._getWeightUnit();
    }
  }

  /** Update weight unit label */
  _toggleWeightUnit() {
    const suffix = this.querySelector('[data-weight-unit-label]');
    if (suffix) {
      suffix.textContent = this._getWeightUnit();
    }
  }

  /** Validate and calculate BMI */
  _handleCalculate() {
    // Clear previous errors
    this.querySelectorAll('.bmi-field__input--error').forEach((el) =>
      el.classList.remove('bmi-field__input--error')
    );
    this.querySelectorAll('.bmi-field__error--visible').forEach((el) =>
      el.classList.remove('bmi-field__error--visible')
    );

    const heightKg = this._getWeightInKg();
    const heightM = this._getHeightInMeters();

    let valid = true;

    if (!heightM || heightM <= 0) {
      this._showFieldError('height');
      valid = false;
    }
    if (!heightKg || heightKg <= 0) {
      this._showFieldError('weight');
      valid = false;
    }

    if (!valid) return;

    const bmi = heightKg / (heightM * heightM);
    const result = BMI_CATEGORIES.find(
      (c) => bmi >= c.range[0] && bmi < c.range[1]
    );

    if (!result) return;

    window.Shopify?.analytics?.publish('custom_bmi_calculated', {
      bmi_value: parseFloat(bmi.toFixed(1)),
      bmi_category: result.category,
    });

    this._displayResult(bmi, result);
    this._showStep(2);
  }

  /**
   * Get height in meters from the form inputs.
   * @returns {number|null}
   */
  _getHeightInMeters() {
    const unit = this._getHeightUnit();

    if (unit === 'cm') {
      const cm = parseFloat(this.querySelector('[data-input="height-cm"]')?.value);
      return cm > 0 ? cm / 100 : null;
    }

    // Imperial: feet + inches
    const feet = parseFloat(this.querySelector('[data-input="height-ft"]')?.value) || 0;
    const inches = parseFloat(this.querySelector('[data-input="height-in"]')?.value) || 0;
    const totalInches = feet * 12 + inches;
    return totalInches > 0 ? totalInches * 0.0254 : null;
  }

  /**
   * Get weight in kg from the form input.
   * @returns {number|null}
   */
  _getWeightInKg() {
    const val = parseFloat(this.querySelector('[data-input="weight"]')?.value);
    if (!val || val <= 0) return null;

    const unit = this._getWeightUnit();
    return unit === 'kg' ? val : val * 0.453592;
  }

  /**
   * Show an error state on a field group.
   * @param {'height'|'weight'} field
   */
  _showFieldError(field) {
    const group = this.querySelector(`[data-field="${field}"]`);
    if (!group) return;
    const input = group.querySelector('.bmi-field__input');
    const error = group.querySelector('.bmi-field__error');
    if (input) input.classList.add('bmi-field__input--error');
    if (error) error.classList.add('bmi-field__error--visible');
  }

  /**
   * Render the BMI result into the step-2 panel.
   * @param {number} bmi
   * @param {Object} category
   */
  _displayResult(bmi, category) {
    if (this.scoreEl) this.scoreEl.textContent = bmi.toFixed(1);
    if (this.rangeEl)
      this.rangeEl.textContent = `${category.range[0]} - ${category.range[1]}`;
    if (this.categoryEl) {
      this.categoryEl.textContent = category.category;
      this.categoryEl.style.color = category.color;
    }
    if (this.descriptionEl) this.descriptionEl.textContent = category.content;

    // Position needle on gauge
    if (this.needleEl) {
      const clampedBmi = Math.min(Math.max(bmi, 0), GAUGE_MAX);
      const pct = (clampedBmi / GAUGE_MAX) * 100;
      this.needleEl.style.left = `${pct}%`;
    }
  }

  /**
   * Switch between step 1 and step 2.
   * @param {1|2} step
   */
  _showStep(step) {
    if (this.step1) {
      this.step1.classList.toggle('bmi-step--active', step === 1);
    }
    if (this.step2) {
      this.step2.classList.toggle('bmi-step--active', step === 2);
    }
    // Scroll to top of section
    this.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /** Reset all form inputs */
  _clearForm() {
    this.form?.reset();
    this._toggleHeightUnit();
    this._toggleWeightUnit();
    this.querySelectorAll('.bmi-field__input--error').forEach((el) =>
      el.classList.remove('bmi-field__input--error')
    );
    this.querySelectorAll('.bmi-field__error--visible').forEach((el) =>
      el.classList.remove('bmi-field__error--visible')
    );
  }

  /** Submit the consultation form */
  async _handleConsultSubmit() {
    if (!this.consultForm) return;

    const submitBtn = this.consultForm.querySelector('button[type="submit"]');
    if (submitBtn) submitBtn.disabled = true;

    // Clear any previous error message
    const consultError = this.querySelector('[data-consult-error]');
    if (consultError) consultError.classList.remove('bmi-consult__error--visible');

    const payload = {
      email: this.consultForm.querySelector('[name="email"]')?.value || '',
      firstName: this.consultForm.querySelector('[name="firstName"]')?.value || '',
      lastName: this.consultForm.querySelector('[name="lastName"]')?.value || '',
      phone: this.consultForm.querySelector('[name="phone"]')?.value || '',
      adminEmail: this.consultForm.querySelector('[name="adminEmail"]')?.value || '',
      senderEmail: this.consultForm.querySelector('[name="senderEmail"]')?.value || '',
    };

    try {
      const response = await fetch('https://e9-utilities.vercel.app/api/bmi', {
        method: 'POST',
        mode: 'cors',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Server responded with status ${response.status}`);
      }

      // Clear form inputs on success
      this.consultForm.querySelectorAll('input:not([type="hidden"])').forEach((input) => {
        input.value = '';
      });

      // Show success message
      if (this.consultSuccess) {
        this.consultSuccess.classList.add('bmi-consult__success--visible');
      }
    } catch (err) {
      // Show error feedback to the user
      if (consultError) {
        consultError.classList.add('bmi-consult__error--visible');
      } else {
        console.error('BMI consultation form submission failed:', err);
      }
    } finally {
      if (submitBtn) submitBtn.disabled = false;
    }
  }
}

if (!customElements.get('bmi-calculator')) {
  customElements.define('bmi-calculator', BmiCalculator);
}
